import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TutorChat.module.css';
import { useIsStaff } from '../../hooks/useIsStaff';
import type { PulseConcept, PulseLens } from '../../services/teachingAgent';

const Row: React.FC<{ label: string; items: PulseConcept[] }> = ({ label, items }) => {
  if (items.length === 0) return null;
  return (
    <div className={styles.pulseRow}>
      <span className={styles.pulseRowLabel}>{label}</span>
      <ul className={styles.pulseChips}>
        {items.map((c) => (
          <li key={c.qid} className={styles.pulseChip} title={c.qid}>
            {c.name}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** The open lesson's paired lab: a real, runnable one. Absent when none exists. */
export interface PulseLabLink {
  /** Real route to the lab (same target the lecture page's "Start Lab" opens). */
  href: string;
  /** Lab number as the lecture page shows it, e.g. "2.1". Optional. */
  number?: string;
  /** In-app open; when given, a plain left click uses it instead of a full page load. */
  onOpen?: () => void;
}

/** The most recent lesson card in a conversation: the card describes the lesson, not one reply. */
export function latestPulse<T extends { pulse?: PulseLens }>(messages: readonly T[]): PulseLens | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const p = messages[i].pulse;
    if (p) return p;
  }
  return undefined;
}

/**
 * What Pulse knows about the open lesson, pinned once above the conversation. Staff only for now: the
 * concept map is still being reviewed, so learners do not see it. Renders nothing without data.
 * `REACT_APP_PULSE_LENS=all` opens it to everyone once the map is approved.
 */
const PulsePanel: React.FC<{ pulse?: PulseLens; lab?: PulseLabLink | null }> = ({ pulse, lab }) => {
  const { t } = useTranslation();
  const staff = useIsStaff();
  const open = staff || process.env.REACT_APP_PULSE_LENS === 'all';
  if (!pulse || !open) return null;
  const learner = pulse.learner;
  return (
    <details className={styles.pulseLens} data-testid="pulse-panel">
      <summary className={styles.pulseSummary}>
        <span className={styles.pulseBadge}>{t('tutor.pulse.title')}</span>
        <span>
          {t('tutor.pulse.subtitle')}
          {pulse.lesson ? ` · ${pulse.lesson}` : ''}
        </span>
      </summary>
      <div className={styles.pulseBody}>
        {pulse.unmapped && pulse.concepts.length === 0 && (
          <p className={styles.pulseMuted}>{t('tutor.pulse.unmapped')}</p>
        )}
        <Row label={t('tutor.pulse.teaches')} items={pulse.concepts} />
        <Row label={t('tutor.pulse.builds_on')} items={pulse.prerequisites} />
        <Row label={t('tutor.pulse.leads_to')} items={pulse.next} />
        <div className={styles.pulseRow}>
          <span className={styles.pulseRowLabel}>{t('tutor.pulse.learner')}</span>
          {learner ? (
            <span className={styles.pulseLearner}>
              {learner.tracked !== undefined && <span>{t('tutor.pulse.tracked', { count: learner.tracked })}</span>}
              {learner.dueReviews !== undefined && <span>{t('tutor.pulse.due', { count: learner.dueReviews })}</span>}
              {learner.goal && <span>{t('tutor.pulse.goal', { goal: learner.goal })}</span>}
            </span>
          ) : (
            <span className={styles.pulseEmpty}>
              <span className={styles.pulseMuted}>{t('tutor.pulse.no_learner')}</span>
              {lab && lab.href && (
                <a
                  className={styles.pulseCta}
                  href={lab.href}
                  data-testid="pulse-lab-cta"
                  onClick={(e) => {
                    if (!lab.onOpen || e.defaultPrevented || e.button !== 0
                      || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    lab.onOpen();
                  }}
                >
                  {lab.number
                    ? t('tutor.pulse.cta_lab_numbered', { number: lab.number })
                    : t('tutor.pulse.cta_lab')}
                  {' '}
                  <span aria-hidden="true">{t('tutor.pulse.cta_arrow')}</span>
                </a>
              )}
            </span>
          )}
        </div>
        <p className={styles.pulseNote}>{t('tutor.pulse.staff_note')}</p>
      </div>
    </details>
  );
};

export default PulsePanel;
